// Google Analytics (gtag.js) helpers. The gtag stub + library are injected in
// pages/_document.tsx; this module just provides typed access to `window.gtag`.

type GtagCommand = "config" | "set" | "event" | "js" | "consent" | "get";

declare global {
  interface Window {
    gtag?: (command: GtagCommand, ...args: any[]) => void;
    dataLayer?: unknown[];
  }
}

interface AnalyticsUser {
  salesAccountID: number | null;
  currentUserID?: number | null;
}

/**
 * Tell Google Analytics who the current user is. Sets a stable `user_id` (the
 * sales account id) so sessions are attributed to the account across devices,
 * plus `user_properties` (sales account id + current user id) for
 * reporting/segmentation.
 *
 * No-op on the server or before gtag.js has defined `window.gtag`. When the
 * library hasn't finished loading the commands are queued on `dataLayer`.
 */
export const identifyAnalyticsUser = ({
  salesAccountID,
  currentUserID,
}: AnalyticsUser): void => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  const userId = salesAccountID != null ? String(salesAccountID) : undefined;

  if (userId) {
    window.gtag("set", { user_id: userId });
  }

  window.gtag("set", "user_properties", {
    sales_account_id: userId,
    current_user_id:
      currentUserID != null ? String(currentUserID) : undefined,
  });
};

type AnalyticsEventParams = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Send a Google Analytics (GA4) custom event. Use snake_case event/param names
 * to match GA conventions, and never pass PII (names, emails, addresses) —
 * stick to carrier/service names, country ISO codes, counts and flags.
 *
 * No-op on the server or before gtag.js has defined `window.gtag`.
 */
export const trackEvent = (
  eventName: string,
  params: AnalyticsEventParams = {},
): void => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  // Analytics is fire-and-forget: it must never throw into the calling feature.
  try {
    window.gtag("event", eventName, params);
  } catch {
    // Swallow — a failed analytics send must not break the user's action.
  }
};

/** Rates fetched for a shipment, or a standalone quote created. */
export const trackRatesFetched = (params: {
  shipmentType: string;
  fromCountry?: string;
  toCountry?: string;
  rateCount: number;
}): void => {
  trackEvent("get_rates", {
    shipment_type: params.shipmentType,
    from_country: params.fromCountry,
    to_country: params.toCountry,
    rate_count: params.rateCount,
  });
};

/** The proof-of-delivery photo in the shipment details panel was clicked. */
export const trackPodImageOpened = (params: {
  carrier?: string;
  hasGps: boolean;
}): void => {
  trackEvent("pod_image_opened", {
    carrier: params.carrier,
    has_gps: params.hasGps,
  });
};

/** The "Open in Google Maps" link on the Status row was clicked. */
export const trackDeliveryMapOpened = (params: { carrier?: string }): void => {
  trackEvent("delivery_map_opened", {
    carrier: params.carrier,
  });
};

/** A shipment was successfully created (or edited). */
export const trackShipmentCreated = (params: {
  carrier?: string;
  service?: string;
  toCountry?: string;
  isEdit: boolean;
  printLater: boolean;
}): void => {
  trackEvent("shipment_created", {
    carrier: params.carrier,
    service: params.service,
    to_country: params.toCountry,
    is_edit: params.isEdit,
    print_later: params.printLater,
  });
};

/** One or more labels were printed. `method` is single | combine | bulk. */
export const trackLabelPrinted = (params: {
  method: "single" | "combine" | "bulk";
  labelCount: number;
}): void => {
  trackEvent("label_printed", {
    method: params.method,
    label_count: params.labelCount,
  });
};

/** A CSV of shipments was imported via bulk shipment creation. */
export const trackBulkShipmentImported = (params: {
  shipmentCount: number;
  validCount: number;
  printLater: boolean;
}): void => {
  trackEvent("bulk_shipment_imported", {
    shipment_count: params.shipmentCount,
    valid_count: params.validCount,
    print_later: params.printLater,
  });
};

/* ------------------------------------------------------------------ */
/* Claims / support wizard                                             */
/* ------------------------------------------------------------------ */

/** The Get support wizard opened (fresh or resuming a draft). */
export const trackSupportWizardOpened = (params: {
  resumedDraft: boolean;
}): void => {
  trackEvent("support_wizard_opened", {
    resumed_draft: params.resumedDraft,
  });
};

/** An issue type was picked on the wizard's first step. */
export const trackSupportIssueSelected = (params: {
  issueType: string;
  claimType?: string | null;
}): void => {
  trackEvent("support_issue_selected", {
    issue_type: params.issueType,
    claim_type: params.claimType ?? undefined,
  });
};

/**
 * The eligibility step reached a verdict for a parcel. `blockedKind` names
 * the dead end (window_closed, no_scan, not_damaged, not_delivered,
 * already_claimed, carrier_blocked, damage_unavailable, not_eligible).
 */
export const trackClaimEligibilityResult = (params: {
  status: "eligible" | "blocked";
  carrier?: string | null;
  claimType?: string | null;
  blockedKind?: string | null;
}): void => {
  trackEvent("claim_eligibility_result", {
    status: params.status,
    carrier: params.carrier ?? undefined,
    claim_type: params.claimType ?? undefined,
    blocked_kind: params.blockedKind ?? undefined,
  });
};

/** A support request was saved as a draft. */
export const trackSupportDraftSaved = (params: {
  issueType: string;
  claimType?: string | null;
}): void => {
  trackEvent("support_draft_saved", {
    issue_type: params.issueType,
    claim_type: params.claimType ?? undefined,
  });
};

/**
 * A pre-submit helper card answered the question before the form.
 * `rule` names the situation (moving-normally, stop-never-scanned, ...),
 * `deflects` whether Connexx thinks no ticket is needed.
 */
export const trackSupportHelperShown = (params: {
  issueType: string;
  rule: string;
  deflects: boolean;
}): void => {
  trackEvent("support_helper_shown", {
    issue_type: params.issueType,
    helper_rule: params.rule,
    deflects: params.deflects,
  });
};

/**
 * The customer clicked "Still need help?" under a helper card that had
 * hidden the form, revealing it. Together with support_helper_shown and
 * support_request_submitted this completes the deflection funnel.
 */
export const trackSupportHelperExpanded = (params: {
  issueType: string;
  rule: string;
}): void => {
  trackEvent("support_helper_expanded", {
    issue_type: params.issueType,
    helper_rule: params.rule,
  });
};

/** A support request was submitted (helperRule = the card they continued past). */
export const trackSupportRequestSubmitted = (params: {
  issueType: string;
  claimType?: string | null;
  carrier?: string | null;
  isClaim: boolean;
  documentCount: number;
  helperRule?: string | null;
}): void => {
  trackEvent("support_request_submitted", {
    issue_type: params.issueType,
    claim_type: params.claimType ?? undefined,
    carrier: params.carrier ?? undefined,
    is_claim: params.isClaim,
    document_count: params.documentCount,
    helper_rule: params.helperRule ?? undefined,
  });
};

/** A dead-end eligibility screen was reported via the issue report link. */
export const trackClaimIssueReported = (params: {
  claimType?: string | null;
  carrier?: string | null;
  hasComments: boolean;
}): void => {
  trackEvent("claim_issue_reported", {
    claim_type: params.claimType ?? undefined,
    carrier: params.carrier ?? undefined,
    has_comments: params.hasComments,
  });
};

/**
 * "View N shipments found in X tab" was clicked in the empty Shipments table
 * (a search with no results on the current tab but some on another).
 */
export const trackSearchOtherTabOpened = (params: {
  fromTab: string;
  toTab: string;
  resultCount: number;
  termLength: number;
}): void => {
  trackEvent("search_other_tab_opened", {
    from_tab: params.fromTab,
    to_tab: params.toTab,
    result_count: params.resultCount,
    term_length: params.termLength,
  });
};

/** "Clear search" was clicked in the empty Shipments table. */
export const trackSearchCleared = (params: {
  fromTab: string;
  termLength: number;
  otherTabResults: number;
}): void => {
  trackEvent("search_cleared", {
    from_tab: params.fromTab,
    term_length: params.termLength,
    other_tab_results: params.otherTabResults,
  });
};
